/* eslint-disable import/no-cycle */
import { CalculatorParticipantType, RuleFundSource } from './CalculatorRule';
import { DisbursementMethod, EmployeeType, SiteRole } from './Employee';

export enum IntegrationType {
  ADORA = 'adora',
  ADP_RUN_EXPORT = 'adp_run_export',
  ALOHA = 'aloha',
  BRINK = 'brink',
  HEARTLAND = 'heartland',
  KICKFIN = 'kickfin',
  ORACLE = 'oracle',
  PAYLOCITY_EXPORT = 'paylocity_export',
  SQUARE = 'square',
  SHIFT4 = 'shift4',
  TOAST = 'toast',
  RESTAURANT365_EXPORT = 'restaurant365_export',
  RPOWER = 'rpower',
  UNION = 'union',
}

export enum IntegrationCategory {
  LABOR = 'labor',
  TICKET = 'ticket',
  PAYROLL = 'payroll',
}

export enum TicketOverrideType {
  IGNORE = 'ignore',
  SPLIT = 'split',
}

export enum IntegrationRefType {
  SITE = 'site',
}

export enum IntegrationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface ISiteIntegration {
  categories: IntegrationCategory[];
  id: string;
  type: IntegrationType;
}

export interface IIntegration extends ISiteIntegration {
  config: IIntegrationConfig[];
  refId: string;
  refType: IntegrationRefType;
  status: IntegrationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface IIntegrationConfig {
  key: string;
  value: string;
  permission: string;
}

export interface IPOSEmployee {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  employeeId?: string;
  job: { title: string };
  type: EmployeeType;
  role: SiteRole;
  disbursementMethod: DisbursementMethod;
}

export interface IPOSProvider {
  type?: string;
  name: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email?: string;
  customerEmployeeId?: string;
  schedules: IPOSProviderSchedule[];
}

export interface IPOSProviderSchedule {
  start: string;
  end: string;
  jobTitle: string;
  totalMinWorked: number;
  clockedOut: boolean;
  breaks: IPOSProviderScheduleBreak[];
}

export interface IPOSProviderScheduleBreak {
  start: string;
  end: string;
}

export interface IPOSTicketCategory {
  name: string;
  price: number;
  quantity: number;
  discounts: number;
  tax: number;
  total: number;
}

export interface IPOSTotal {
  totalMinWorked: number;
  ticketCount: number;
  total: number;
  creditCard: number;
  cash: number;
  creditCardFee: number;
  categories?: IPOSTicketCategory[];
}

export interface IPOSTotalWithUnallocated extends IPOSSegmentTotals {
  unallocated?: IPOSSegmentTotals;
}

export interface IPOSTicketEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  customerEmployeeId: string;
}

export interface ISplitParticipant {
  id: string;
  name: string;
  type: CalculatorParticipantType;
}

export interface IPOSTicketSplitOverrideShare {
  participant: ISplitParticipant;
  percent: number;
  appliedPercent?: number;
  gross: number;
  tip: number;
  autoGratuity: number;
  sources: RuleFundSource[];
}

export interface IPOSTicketSplitOverride {
  type: TicketOverrideType.SPLIT;
  shares: IPOSTicketSplitOverrideShare[];
}

export interface IPOSTicketIgnoreOverride {
  type: TicketOverrideType.IGNORE;
}

export interface IPOSTicket {
  ticketId: string;
  displayId: string;
  employeeId?: string;
  employee?: IPOSTicketEmployee;
  forcedMatch: boolean;
  openedAt: string;
  closedAt: string;
  paidAt: string;
  processingTime: string;
  net: number;
  gross: number;
  categories?: IPOSTicketCategory[];
  override?: IPOSTicketIgnoreOverride | IPOSTicketSplitOverride;
  totals: IPOSTicketTotals;
}

interface IPOSBaseTotals {
  gross: number;
  net: number;
  tip: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
  autoGratuity: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
}

export interface IPOSTicketTotals extends IPOSBaseTotals {}

export interface IPOSSegmentTotals extends IPOSBaseTotals {
  ticketCount: number;
  totalMinWorked: number;
}

export interface IPOSSegment {
  id: string;
  name: string;
  job: { title: string };
  tickets: IPOSTicket[];
  totals: IPOSSegmentTotals;
}

export interface IPOSData {
  employee?: IPOSEmployee;
  totals: IPOSTotalWithUnallocated;
  segments: IPOSSegment[];
  provider: IPOSProvider;
}

export interface IPOSResults {
  date: string;
  data: IPOSData[];
}

export interface IPOSCategory {
  name: string;
}

export interface IPOSCategories {
  categories: IPOSCategory[];
}

export interface IConnection {
  id: string;
  provider: {
    id: string;
    name: string;
  };
}

export interface IOrganizationConnection {
  organization: {
    id: string;
    name: string;
  };
  connections: IConnection[];
}

export interface IOauthURL {
  url: string;
  state?: string;
}

export interface IOauthCallbackPayload {
  organizationId: string;
  code: string;
  state: string;
}

interface IUnclosedTicket {
  ticketId: string;
  displayId: string;
  employee?: {
    firstName: string;
    lastName: string;
  };
  amount: number;
}

export interface IPosUnclosedChecksError {
  code: string;
  msg: string;
  tickets: IUnclosedTicket[];
}
